import { eventsService, Event } from './eventsService';

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  type: 'text' | 'event_card' | 'quick_reply' | 'booking_form';
  data?: any;
}

interface ConversationContext {
  userId: string;
  currentFlow: 'welcome' | 'event_discovery' | 'booking' | 'support' | 'feedback';
  userData: {
    name?: string;
    email?: string;
    phone?: string;
    preferences?: string[];
    preferredLanguage?: 'ar' | 'en';
  };
  selectedEvent?: Event;
  bookingData?: any;
}

interface QuickReply {
  text: string;
  action: string;
  payload?: any;
}

class WhatsAppChatService {
  private conversations: Map<string, ConversationContext> = new Map();
  private messages: Map<string, ChatMessage[]> = new Map();

  // Initialize conversation for a user
  initializeConversation(userId: string): ChatMessage[] {
    if (!this.conversations.has(userId)) {
      this.conversations.set(userId, {
        userId,
        currentFlow: 'welcome',
        userData: {}
      });
      this.messages.set(userId, []);
    }

    // Send welcome message if it's a new conversation
    const messages = this.messages.get(userId)!;
    if (messages.length === 0) {
      this.addBotMessage(userId, {
        text: "👋 Welcome to Jordan's #1 Event Platform! I'm your AI assistant.\n\nI can help you with:\n🎫 Discover amazing events\n🎟️ Book tickets instantly\n💬 Answer your questions\n🎯 Get personalized recommendations\n\nHow can I assist you today?",
        type: 'text',
        quickReplies: [
          { text: "🔍 Find Events", action: "discover_events" },
          { text: "🎵 Music Events", action: "filter_music" },
          { text: "🏢 Business Events", action: "filter_business" },
          { text: "🌐 العربية", action: "switch_arabic" },
          { text: "❓ Help & Support", action: "support" }
        ]
      });
    }

    return this.messages.get(userId)!;
  }

  // Process user message and generate response
  async processMessage(userId: string, userMessage: string): Promise<ChatMessage[]> {
    const context = this.conversations.get(userId);
    if (!context) {
      return this.initializeConversation(userId);
    }

    // Add user message
    this.addUserMessage(userId, userMessage);

    // Detect language and process message
    const isArabic = this.detectArabicContent(userMessage);
    context.userData.preferredLanguage = isArabic ? 'ar' : 'en';

    // Process based on current flow and message content
    await this.generateResponse(userId, userMessage, context);

    return this.messages.get(userId)!;
  }

  // Process quick reply actions
  async processQuickReply(userId: string, action: string, payload?: any): Promise<ChatMessage[]> {
    const context = this.conversations.get(userId);
    if (!context) {
      return this.initializeConversation(userId);
    }

    await this.handleQuickReplyAction(userId, action, payload, context);
    return this.messages.get(userId)!;
  }

  private async generateResponse(userId: string, userMessage: string, context: ConversationContext) {
    const lowerMessage = userMessage.toLowerCase();

    // Check for specific event name searches (like "Bridges")
    const eventSearchResult = await this.searchEventByName(userMessage);
    if (eventSearchResult) {
      await this.handleSpecificEventQuery(userId, context, eventSearchResult, userMessage);
      return;
    }

    // Detect specific category intent first (more specific)
    const categoryIntent = this.detectCategoryIntent(userMessage);
    if (categoryIntent) {
      await this.handleCategorySpecificRequest(userId, context, categoryIntent, userMessage);
      return;
    }

    // General intent detection (less specific)
    if (this.isEventDiscoveryIntent(lowerMessage) || this.isArabicEventIntent(userMessage)) {
      await this.handleEventDiscovery(userId, context, userMessage);
    } else if (this.isBookingIntent(lowerMessage) || this.isArabicBookingIntent(userMessage)) {
      // For booking intent, try to determine category first
      const bookingCategory = this.detectCategoryIntent(userMessage);
      if (bookingCategory) {
        await this.handleCategorySpecificRequest(userId, context, bookingCategory, userMessage);
      } else {
        await this.handleEventDiscovery(userId, context, userMessage);
      }
    } else if (this.isSupportIntent(lowerMessage) || this.isArabicSupportIntent(userMessage)) {
      await this.handleSupport(userId, context, userMessage);
    } else if (this.isLocationRequest(userMessage)) {
      await this.handleLocationRequest(userId, context, userMessage);
    } else if (this.isGreeting(lowerMessage) || this.isArabicGreeting(userMessage)) {
      await this.handleGreeting(userId, context);
    } else {
      await this.handleGeneralQuery(userId, context, userMessage);
    }
  }

  private async handleQuickReplyAction(userId: string, action: string, payload: any, context: ConversationContext) {
    switch (action) {
      case 'discover_events':
        await this.handleEventDiscovery(userId, context);
        break;
      case 'filter_music':
        await this.handleCategoryFilter(userId, context, 'Music');
        break;
      case 'filter_business':
        await this.handleCategoryFilter(userId, context, 'Business');
        break;
      case 'filter_sports':
        await this.handleCategoryFilter(userId, context, 'Sports');
        break;
      case 'filter_arts':
        await this.handleCategoryFilter(userId, context, 'Arts');
        break;
      case 'support':
        await this.handleSupport(userId, context);
        break;
      case 'book_event':
        await this.handleEventBooking(userId, context, payload);
        break;
      case 'view_event_details':
        await this.handleEventDetails(userId, context, payload);
        break;
      case 'more_events':
        await this.handleEventDiscovery(userId, context);
        break;
      case 'restart':
        await this.restartConversation(userId);
        break;
      case 'open_map':
        await this.handleOpenMap(userId, context, payload);
        break;
      case 'show_venue_location':
        await this.handleShowVenueLocation(userId, context, payload);
        break;
      case 'switch_arabic':
        await this.handleLanguageSwitch(userId, context, 'ar');
        break;
      case 'switch_english':
        await this.handleLanguageSwitch(userId, context, 'en');
        break;
      default:
        await this.handleGeneralQuery(userId, context, action);
    }
  }

  private async handleEventDiscovery(userId: string, context: ConversationContext, query?: string) {
    context.currentFlow = 'event_discovery';
    
    this.addBotMessage(userId, {
      text: "🎭 Let me show you some amazing events happening soon!",
      type: 'text'
    });

    const events = await eventsService.getPublishedEvents();
    
    if (events.length === 0) {
      this.addBotMessage(userId, {
        text: "🔍 No events are currently available, but new exciting events are added regularly! Would you like me to notify you when new events are posted?",
        type: 'text',
        quickReplies: [
          { text: "🔔 Yes, notify me", action: "subscribe_notifications" },
          { text: "📞 Contact Support", action: "support" }
        ]
      });
      return;
    }

    // Show featured events (limit to 3 for better UX)
    const featuredEvents = events.slice(0, 3);
    
    featuredEvents.forEach(event => {
      this.addBotMessage(userId, {
        text: `🎪 ${event.title}`,
        type: 'event_card',
        data: {
          event,
          quickReplies: [
            { text: "🎟️ Book Tickets", action: "book_event", payload: event.id },
            { text: "📋 View Details", action: "view_event_details", payload: event.id }
          ]
        }
      });
    });

    this.addBotMessage(userId, {
      text: "✨ Which event interests you? Or would you like to see more options?",
      type: 'text',
      quickReplies: [
        { text: "🎵 Music Events", action: "filter_music" },
        { text: "🏢 Business Events", action: "filter_business" },
        { text: "🎨 Arts & Culture", action: "filter_arts" },
        { text: "🏆 Sports Events", action: "filter_sports" },
        { text: "👀 More Events", action: "more_events" }
      ]
    });
  }

  private async handleCategoryFilter(userId: string, context: ConversationContext, category: string) {
    const events = await eventsService.getEventsByCategory(category);
    
    if (events.length === 0) {
      this.addBotMessage(userId, {
        text: `🔍 No ${category} events are currently available. Would you like to explore other categories?`,
        type: 'text',
        quickReplies: [
          { text: "🎵 Music", action: "filter_music" },
          { text: "🏢 Business", action: "filter_business" },
          { text: "🎨 Arts", action: "filter_arts" },
          { text: "🔍 All Events", action: "discover_events" }
        ]
      });
      return;
    }

    this.addBotMessage(userId, {
      text: `🎯 Here are the best ${category} events for you:`,
      type: 'text'
    });

    events.slice(0, 3).forEach(event => {
      this.addBotMessage(userId, {
        text: `${this.getCategoryIcon(category)} ${event.title}`,
        type: 'event_card',
        data: {
          event,
          quickReplies: [
            { text: "🎟️ Book Now", action: "book_event", payload: event.id },
            { text: "📋 Details", action: "view_event_details", payload: event.id }
          ]
        }
      });
    });

    this.addBotMessage(userId, {
      text: "💡 Ready to book or need more information?",
      type: 'text',
      quickReplies: [
        { text: "🔍 Other Categories", action: "discover_events" },
        { text: "❓ I have questions", action: "support" }
      ]
    });
  }

  private async handleEventBooking(userId: string, context: ConversationContext, eventId: string) {
    const event = await eventsService.getEventById(eventId);
    
    if (!event) {
      this.addBotMessage(userId, {
        text: "❌ Sorry, this event is no longer available. Let me show you other events!",
        type: 'text',
        quickReplies: [
          { text: "🔍 Browse Events", action: "discover_events" }
        ]
      });
      return;
    }

    context.selectedEvent = event;
    context.currentFlow = 'booking';

    this.addBotMessage(userId, {
      text: `🎟️ Great choice! You're booking: *${event.title}*\n\n📅 ${event.date} at ${event.time}\n📍 ${event.venue}\n💰 $${event.price} per ticket\n\nTo complete your booking, I'll redirect you to our secure checkout page where you can:\n\n✅ Enter your details\n✅ Choose payment method (Apple Pay, Google Pay, Card, Crypto)\n✅ Get instant ticket confirmation\n✅ Receive NFC-enabled mobile tickets`,
      type: 'text',
      quickReplies: [
        { text: "🚀 Continue to Checkout", action: "redirect_checkout", payload: event.id },
        { text: "📋 Event Details", action: "view_event_details", payload: event.id },
        { text: "🔍 Other Events", action: "discover_events" }
      ]
    });
  }

  private async handleEventDetails(userId: string, context: ConversationContext, eventId: string) {
    const event = await eventsService.getEventById(eventId);
    
    if (!event) {
      this.addBotMessage(userId, {
        text: "❌ Event not found. Let me show you available events!",
        type: 'text',
        quickReplies: [
          { text: "🔍 Browse Events", action: "discover_events" }
        ]
      });
      return;
    }

    const soldPercentage = Math.round((event.ticketsSold / event.capacity) * 100);
    const availableTickets = event.capacity - event.ticketsSold;

    this.addBotMessage(userId, {
      text: `📋 *${event.title}*\n\n📝 ${event.description}\n\n📅 *Date:* ${event.date}\n⏰ *Time:* ${event.time}\n📍 *Venue:* ${event.venue}\n💰 *Price:* $${event.price}\n🎫 *Available:* ${availableTickets} tickets\n📊 *Popularity:* ${soldPercentage}% sold\n\n🏷️ *Tags:* ${event.tags.join(', ')}`,
      type: 'text',
      quickReplies: [
        { text: "🎟️ Book Tickets", action: "book_event", payload: event.id },
        { text: "🔍 Similar Events", action: "discover_events" },
        { text: "❓ Ask Questions", action: "support" }
      ]
    });
  }

  private async handleSupport(userId: string, context: ConversationContext, query?: string) {
    context.currentFlow = 'support';

    if (query) {
      // Try to answer specific questions
      const answer = this.getAnswerForQuery(query);
      if (answer) {
        this.addBotMessage(userId, {
          text: answer,
          type: 'text',
          quickReplies: [
            { text: "✅ Helpful", action: "support_feedback_positive" },
            { text: "❓ More Help", action: "support" },
            { text: "🔍 Browse Events", action: "discover_events" }
          ]
        });
        return;
      }
    }

    this.addBotMessage(userId, {
      text: "🤝 I'm here to help! What do you need assistance with?",
      type: 'text',
      quickReplies: [
        { text: "🎟️ Ticket Booking", action: "support_booking" },
        { text: "💳 Payment Issues", action: "support_payment" },
        { text: "📱 Mobile Tickets", action: "support_mobile" },
        { text: "🔄 Refunds", action: "support_refunds" },
        { text: "📍 Venue Info", action: "support_venue" },
        { text: "👤 Contact Human", action: "contact_human" }
      ]
    });
  }

  private async handleGreeting(userId: string, context: ConversationContext) {
    const greetings = [
      "👋 Hello! Ready to discover amazing events?",
      "🎉 Hi there! What exciting event are you looking for today?",
      "✨ Hey! I'm here to help you find the perfect event!"
    ];

    const randomGreeting = greetings[Math.floor(Math.random() * greetings.length)];

    this.addBotMessage(userId, {
      text: randomGreeting,
      type: 'text',
      quickReplies: [
        { text: "🔍 Find Events", action: "discover_events" },
        { text: "🎵 Music Events", action: "filter_music" },
        { text: "❓ Help", action: "support" }
      ]
    });
  }

  private async handleGeneralQuery(userId: string, context: ConversationContext, query: string) {
    // AI-powered response generation based on query
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('price') || lowerQuery.includes('cost') || lowerQuery.includes('ticket')) {
      this.addBotMessage(userId, {
        text: "💰 Our ticket prices vary by event. Most events range from $25-$200.\n\n✨ Features included:\n• Mobile NFC tickets\n• Instant confirmation\n• Multiple payment options\n• 24/7 customer support\n\nWould you like to see current events and their prices?",
        type: 'text',
        quickReplies: [
          { text: "🔍 View Events & Prices", action: "discover_events" },
          { text: "💳 Payment Options", action: "support_payment" }
        ]
      });
    } else if (lowerQuery.includes('location') || lowerQuery.includes('venue') || lowerQuery.includes('where')) {
      this.addBotMessage(userId, {
        text: "📍 We host events across Jordan's best venues:\n\n🏟️ Amman Arena\n🎭 Royal Cultural Center\n🏢 King Hussein Convention Center\n🎪 Citadel Amphitheater\n🌟 And many more!\n\nEach event page shows the exact venue location and directions.",
        type: 'text',
        quickReplies: [
          { text: "🔍 Browse by Venue", action: "discover_events" },
          { text: "📍 Venue Details", action: "support_venue" }
        ]
      });
    } else if (lowerQuery.includes('time') || lowerQuery.includes('when') || lowerQuery.includes('date')) {
      this.addBotMessage(userId, {
        text: "📅 We have events happening all the time!\n\n🌅 Morning events (9 AM - 12 PM)\n🌞 Afternoon events (1 PM - 5 PM)\n🌙 Evening events (6 PM - 11 PM)\n\nMost events are on weekends, but we also have weekday business events and conferences.",
        type: 'text',
        quickReplies: [
          { text: "📅 This Weekend", action: "discover_events" },
          { text: "🏢 Weekday Events", action: "filter_business" }
        ]
      });
    } else {
      // General AI-style response
      this.addBotMessage(userId, {
        text: "🤔 I understand you're asking about events! I'm here to help you find the perfect experience.\n\nI can assist you with:\n🎫 Finding events by category\n📅 Checking dates and times\n💰 Pricing information\n📍 Venue details\n🎟️ Booking tickets\n\nWhat specifically would you like to know?",
        type: 'text',
        quickReplies: [
          { text: "🔍 Find Events", action: "discover_events" },
          { text: "❓ Ask Specific Question", action: "support" },
          { text: "🎟️ How to Book", action: "support_booking" }
        ]
      });
    }
  }

  private getAnswerForQuery(query: string): string | null {
    const lowerQuery = query.toLowerCase();
    
    const faqAnswers: Record<string, string> = {
      'how to book': "🎟️ Booking is super easy!\n\n1️⃣ Browse events\n2️⃣ Click 'Book Tickets'\n3️⃣ Fill your details\n4️⃣ Choose payment (Apple Pay, Google Pay, Card, Crypto)\n5️⃣ Get instant mobile tickets!\n\nYour tickets are NFC-enabled for quick venue entry.",
      'payment methods': "💳 We accept all major payment methods:\n\n📱 Apple Pay & Google Pay\n💳 Credit & Debit Cards\n₿ Cryptocurrency\n💰 Cash (at select venues)\n🏪 Bank Transfer\n\nAll payments are 100% secure and encrypted.",
      'refund policy': "🔄 Our refund policy:\n\n✅ Full refund if event is cancelled\n✅ 80% refund up to 48 hours before event\n✅ 50% refund up to 24 hours before event\n❌ No refunds within 24 hours of event\n\n🎫 Tickets can be transferred to friends anytime!",
      'mobile tickets': "📱 Your mobile tickets include:\n\n🎫 QR code for entry\n📡 NFC technology for tap entry\n📍 Venue maps and directions\n⏰ Event reminders\n🎪 Exclusive event updates\n\nNo need to print - just show your phone!",
      'venue location': "📍 All our venues are easily accessible:\n\n🚗 Parking available\n🚌 Public transport links\n🚕 Taxi/Uber friendly\n♿ Wheelchair accessible\n🍕 Food and drinks on-site\n\nDetailed directions sent with your ticket!"
    };

    for (const [key, answer] of Object.entries(faqAnswers)) {
      if (lowerQuery.includes(key)) {
        return answer;
      }
    }

    return null;
  }

  private isEventDiscoveryIntent(message: string): boolean {
    const keywords = ['event', 'find', 'search', 'discover', 'show', 'browse', 'what'];
    return keywords.some(keyword => message.includes(keyword));
  }

  private isBookingIntent(message: string): boolean {
    const keywords = ['book', 'buy', 'ticket', 'purchase', 'reserve'];
    return keywords.some(keyword => message.includes(keyword));
  }

  private isSupportIntent(message: string): boolean {
    const keywords = ['help', 'support', 'question', 'problem', 'issue', 'how'];
    return keywords.some(keyword => message.includes(keyword));
  }

  private isGreeting(message: string): boolean {
    const keywords = ['hi', 'hello', 'hey', 'good morning', 'good afternoon', 'good evening'];
    return keywords.some(keyword => message.includes(keyword));
  }

  private getCategoryIcon(category: string): string {
    const icons: Record<string, string> = {
      'Music': '🎵',
      'Business': '🏢',
      'Sports': '🏆',
      'Arts': '🎨',
      'Food': '🍽️',
      'Tech': '💻',
      'Festival': '🎪'
    };
    return icons[category] || '🎭';
  }

  private addUserMessage(userId: string, text: string) {
    const messages = this.messages.get(userId) || [];
    messages.push({
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date(),
      type: 'text'
    });
    this.messages.set(userId, messages);
  }

  private addBotMessage(userId: string, config: {
    text: string;
    type: 'text' | 'event_card' | 'quick_reply';
    data?: any;
    quickReplies?: QuickReply[];
  }) {
    const messages = this.messages.get(userId) || [];
    messages.push({
      id: Date.now().toString() + '_bot',
      text: config.text,
      sender: 'bot',
      timestamp: new Date(),
      type: config.type,
      data: { ...config.data, quickReplies: config.quickReplies }
    });
    this.messages.set(userId, messages);
  }

  private async restartConversation(userId: string) {
    this.conversations.delete(userId);
    this.messages.delete(userId);
    this.initializeConversation(userId);
  }

  // Public methods for external integration
  public getConversationHistory(userId: string): ChatMessage[] {
    return this.messages.get(userId) || [];
  }

  public clearConversation(userId: string): void {
    this.conversations.delete(userId);
    this.messages.delete(userId);
  }

  public updateUserData(userId: string, userData: any): void {
    const context = this.conversations.get(userId);
    if (context) {
      context.userData = { ...context.userData, ...userData };
    }
  }

  // Integration with booking system
  public async handleBookingRedirect(userId: string, eventId: string): Promise<string> {
    const event = await eventsService.getEventById(eventId);
    if (!event) {
      return '/';
    }
    
    // Save booking context
    const context = this.conversations.get(userId);
    if (context) {
      context.selectedEvent = event;
      context.currentFlow = 'booking';
    }

    // Return checkout URL with event ID
    return `/checkout?eventId=${eventId}&source=whatsapp`;
  }

  // Arabic language detection and support methods
  private detectArabicContent(message: string): boolean {
    // Check for Arabic characters
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F]/;
    return arabicRegex.test(message);
  }

  private async searchEventByName(message: string): Promise<Event | null> {
    const events = await eventsService.getPublishedEvents();
    const searchTerms = message.toLowerCase();
    
    // Search for events by title (supports partial matching)
    for (const event of events) {
      const eventTitle = event.title.toLowerCase();
      // Check if any word in the event title is mentioned in the message
      const titleWords = eventTitle.split(' ');
      if (titleWords.some(word => word.length > 3 && searchTerms.includes(word))) {
        return event;
      }
      
      // Special case for "Bridges" - matches "Calum Scott Bridges"
      if (searchTerms.includes('bridges') && eventTitle.includes('bridges')) {
        return event;
      }
    }
    
    return null;
  }

  private async handleSpecificEventQuery(userId: string, context: ConversationContext, event: Event, originalMessage: string) {
    const isLocationRequest = this.isLocationRequest(originalMessage);
    
    if (isLocationRequest) {
      // Handle location request specifically
      await this.handleEventLocationRequest(userId, context, event);
    } else {
      // General event information
      await this.handleEventDetails(userId, context, event.id);
    }
  }

  private async handleEventLocationRequest(userId: string, context: ConversationContext, event: Event) {
    const isArabic = context.userData.preferredLanguage === 'ar';
    
    const locationMessage = isArabic 
      ? `📍 موقع حفلة ${event.title}:\n\n🏢 المكان: ${event.venue}\n📅 التاريخ: ${event.date}\n⏰ الوقت: ${event.time}\n\nسأرسل لك رابط الخريطة لسهولة الوصول 🗺️`
      : `📍 Location for ${event.title}:\n\n🏢 Venue: ${event.venue}\n📅 Date: ${event.date}\n⏰ Time: ${event.time}\n\nI'll send you the map link for easy navigation 🗺️`;

    this.addBotMessage(userId, {
      text: locationMessage,
      type: 'text',
      quickReplies: [
        { 
          text: isArabic ? "🗺️ فتح الخريطة" : "🗺️ Open Map", 
          action: "open_map", 
          payload: { venue: event.venue, eventId: event.id } 
        },
        { 
          text: isArabic ? "🎟️ حجز التذاكر" : "🎟️ Book Tickets", 
          action: "book_event", 
          payload: event.id 
        },
        { 
          text: isArabic ? "📋 تفاصيل الحفلة" : "📋 Event Details", 
          action: "view_event_details", 
          payload: event.id 
        },
        { 
          text: isArabic ? "🔍 حفلات أخرى" : "🔍 Other Events", 
          action: "discover_events" 
        }
      ]
    });
  }

  private async handleLocationRequest(userId: string, context: ConversationContext, message: string) {
    const isArabic = context.userData.preferredLanguage === 'ar';
    
    // If no specific event mentioned, show all venue locations
    const events = (await eventsService.getPublishedEvents()).slice(0, 3);
    
    const locationMessage = isArabic 
      ? "📍 إليك مواقع الحفلات المتاحة:"
      : "📍 Here are the available event locations:";

    this.addBotMessage(userId, {
      text: locationMessage,
      type: 'text'
    });

    events.forEach(event => {
      this.addBotMessage(userId, {
        text: isArabic 
          ? `🎪 ${event.title}\n📍 ${event.venue}\n📅 ${event.date}`
          : `🎪 ${event.title}\n📍 ${event.venue}\n📅 ${event.date}`,
        type: 'event_card',
        data: {
          event,
          quickReplies: [
            { 
              text: isArabic ? "🗺️ موقع المكان" : "🗺️ Venue Location", 
              action: "show_venue_location", 
              payload: event.id 
            },
            { 
              text: isArabic ? "🎟️ حجز" : "🎟️ Book", 
              action: "book_event", 
              payload: event.id 
            }
          ]
        }
      });
    });
  }

  // Arabic intent detection methods
  private isArabicEventIntent(message: string): boolean {
    const arabicEventKeywords = ['حفلة', 'حفلات', 'عرض', 'عروض', 'مناسبة', 'مناسبات', 'فعالية', 'فعاليات', 'ابحث', 'اريد', 'ابي', 'دور', 'اعرض'];
    return arabicEventKeywords.some(keyword => message.includes(keyword));
  }

  private isArabicBookingIntent(message: string): boolean {
    const arabicBookingKeywords = ['حجز', 'احجز', 'اشتري', 'تذكرة', 'تذاكر', 'تذكرتي', 'شراء'];
    return arabicBookingKeywords.some(keyword => message.includes(keyword));
  }

  private isArabicSupportIntent(message: string): boolean {
    const arabicSupportKeywords = ['مساعدة', 'ساعدني', 'مساعده', 'سؤال', 'استفسار', 'مشكلة', 'مشكله', 'كيف', 'ازاي', 'ليش', 'وين'];
    return arabicSupportKeywords.some(keyword => message.includes(keyword));
  }

  private isArabicGreeting(message: string): boolean {
    const arabicGreetings = ['سلام', 'السلام', 'مرحبا', 'مرحباً', 'اهلا', 'أهلا', 'أهلاً', 'اهلين', 'هلا', 'هلو', 'هاي', 'صباح', 'مساء'];
    return arabicGreetings.some(greeting => message.includes(greeting));
  }

  private isLocationRequest(message: string): boolean {
    const locationKeywords = ['location', 'venue', 'where', 'address', 'map', 'directions', 'place'];
    const arabicLocationKeywords = ['موقع', 'مكان', 'عنوان', 'وين', 'فين', 'خريطة', 'مكانه', 'مكانها', 'ابعثلي'];
    
    const lowerMessage = message.toLowerCase();
    return locationKeywords.some(keyword => lowerMessage.includes(keyword)) || 
           arabicLocationKeywords.some(keyword => message.includes(keyword));
  }

  // Enhanced category detection with better accuracy
  private detectCategoryIntent(message: string): string | null {
    const lowerMessage = message.toLowerCase();
    
    // Sports detection (English and Arabic)
    const sportsKeywords = ['sport', 'sports', 'football', 'soccer', 'basketball', 'tennis', 'athletics', 'marathon', 'game', 'match', 'tournament', 'championship'];
    const arabicSportsKeywords = ['رياضة', 'رياضي', 'رياضية', 'كرة', 'قدم', 'سلة', 'تنس', 'رياضات', 'مباراة', 'مباريات', 'بطولة', 'منافسة'];
    
    // Music detection (English and Arabic)
    const musicKeywords = ['music', 'concert', 'singer', 'band', 'song', 'musical', 'artist', 'performance', 'show', 'tour'];
    const arabicMusicKeywords = ['موسيقى', 'موسيقي', 'حفلة', 'حفل', 'مغني', 'مغنية', 'فرقة', 'أغنية', 'عرض', 'جولة', 'فنان', 'فنانة'];
    
    // Business detection (English and Arabic)
    const businessKeywords = ['business', 'conference', 'seminar', 'workshop', 'meeting', 'networking', 'corporate', 'professional', 'training'];
    const arabicBusinessKeywords = ['أعمال', 'مؤتمر', 'ندوة', 'ورشة', 'عمل', 'اجتماع', 'تدريب', 'مهني', 'شركات', 'تجاري'];
    
    // Arts detection (English and Arabic)
    const artsKeywords = ['art', 'arts', 'exhibition', 'gallery', 'theater', 'theatre', 'drama', 'culture', 'cultural'];
    const arabicArtsKeywords = ['فن', 'فنون', 'معرض', 'مسرح', 'ثقافة', 'ثقافي', 'تشكيلي', 'أدب'];
    
    // Check for sports
    if (sportsKeywords.some(keyword => lowerMessage.includes(keyword)) || 
        arabicSportsKeywords.some(keyword => message.includes(keyword))) {
      return 'Sports';
    }
    
    // Check for music
    if (musicKeywords.some(keyword => lowerMessage.includes(keyword)) || 
        arabicMusicKeywords.some(keyword => message.includes(keyword))) {
      return 'Music';
    }
    
    // Check for business
    if (businessKeywords.some(keyword => lowerMessage.includes(keyword)) || 
        arabicBusinessKeywords.some(keyword => message.includes(keyword))) {
      return 'Business';
    }
    
    // Check for arts
    if (artsKeywords.some(keyword => lowerMessage.includes(keyword)) || 
        arabicArtsKeywords.some(keyword => message.includes(keyword))) {
      return 'Arts';
    }
    
    return null;
  }

  private async handleCategorySpecificRequest(userId: string, context: ConversationContext, category: string, originalMessage: string) {
    const isArabic = context.userData.preferredLanguage === 'ar';
    const events = await eventsService.getEventsByCategory(category);
    
    if (events.length === 0) {
      const noEventsMessage = isArabic 
        ? `🔍 عذراً، لا توجد فعاليات ${this.getCategoryNameArabic(category)} متاحة حالياً.\n\nهل تريد استكشاف فئات أخرى؟`
        : `🔍 Sorry, no ${category} events are currently available.\n\nWould you like to explore other categories?`;
      
      this.addBotMessage(userId, {
        text: noEventsMessage,
        type: 'text',
        quickReplies: [
          { text: isArabic ? "🎵 موسيقى" : "🎵 Music", action: "filter_music" },
          { text: isArabic ? "🏢 أعمال" : "🏢 Business", action: "filter_business" },
          { text: isArabic ? "🎨 فنون" : "🎨 Arts", action: "filter_arts" },
          { text: isArabic ? "🔍 جميع الفعاليات" : "🔍 All Events", action: "discover_events" }
        ]
      });
      return;
    }

    const categoryMessage = isArabic 
      ? `🎯 وجدت ${events.length} فعالية ${this.getCategoryNameArabic(category)} رائعة لك:`
      : `🎯 Found ${events.length} amazing ${category} events for you:`;

    this.addBotMessage(userId, {
      text: categoryMessage,
      type: 'text'
    });

    // Show up to 3 events from the specific category
    events.slice(0, 3).forEach(event => {
      this.addBotMessage(userId, {
        text: `${this.getCategoryIcon(category)} ${event.title}`,
        type: 'event_card',
        data: {
          event,
          quickReplies: [
            { 
              text: isArabic ? "🎟️ احجز الآن" : "🎟️ Book Now", 
              action: "book_event", 
              payload: event.id 
            },
            { 
              text: isArabic ? "📋 التفاصيل" : "📋 Details", 
              action: "view_event_details", 
              payload: event.id 
            }
          ]
        }
      });
    });

    const moreEventsMessage = isArabic 
      ? "💡 هل تريد رؤية المزيد أم تفضل فئة أخرى؟"
      : "💡 Would you like to see more or explore other categories?";

    this.addBotMessage(userId, {
      text: moreEventsMessage,
      type: 'text',
      quickReplies: [
        { 
          text: isArabic ? `🔍 المزيد من ${this.getCategoryNameArabic(category)}` : `🔍 More ${category}`, 
          action: `filter_${category.toLowerCase()}` 
        },
        { text: isArabic ? "🎵 موسيقى" : "🎵 Music", action: "filter_music" },
        { text: isArabic ? "🏢 أعمال" : "🏢 Business", action: "filter_business" },
        { text: isArabic ? "🎨 فنون" : "🎨 Arts", action: "filter_arts" },
        { text: isArabic ? "❓ مساعدة" : "❓ Help", action: "support" }
      ]
    });
  }

  private getCategoryNameArabic(category: string): string {
    const arabicNames: Record<string, string> = {
      'Music': 'الموسيقى',
      'Sports': 'الرياضة',
      'Business': 'الأعمال',
      'Arts': 'الفنون',
      'Food': 'الطعام',
      'Tech': 'التكنولوجيا',
      'Festival': 'المهرجانات'
    };
    return arabicNames[category] || category;
  }

  private async handleOpenMap(userId: string, context: ConversationContext, payload: any) {
    const isArabic = context.userData.preferredLanguage === 'ar';
    const venue = payload?.venue || 'Amman Arena';
    
    // Generate map URL (Google Maps)
    const mapUrl = `https://www.google.com/maps/search/${encodeURIComponent(venue + ' Amman Jordan')}`;
    
    const mapMessage = isArabic 
      ? `🗺️ رابط الخريطة لـ ${venue}:\n\n${mapUrl}\n\nيمكنك الضغط على الرابط لفتح الخريطة والحصول على الاتجاهات 📍`
      : `🗺️ Map link for ${venue}:\n\n${mapUrl}\n\nClick the link to open the map and get directions 📍`;

    this.addBotMessage(userId, {
      text: mapMessage,
      type: 'text',
      quickReplies: [
        { 
          text: isArabic ? "🎟️ حجز التذاكر" : "🎟️ Book Tickets", 
          action: "book_event", 
          payload: payload?.eventId 
        },
        { 
          text: isArabic ? "📞 التواصل مع المكان" : "📞 Contact Venue", 
          action: "contact_venue", 
          payload: payload?.venue 
        },
        { 
          text: isArabic ? "🔍 حفلات أخرى" : "🔍 Other Events", 
          action: "discover_events" 
        }
      ]
    });
  }

  private async handleShowVenueLocation(userId: string, context: ConversationContext, eventId: string) {
    const event = await eventsService.getEventById(eventId);
    if (!event) {
      const isArabic = context.userData.preferredLanguage === 'ar';
      this.addBotMessage(userId, {
        text: isArabic ? "❌ عذراً، لم أجد معلومات عن هذه الحفلة" : "❌ Sorry, I couldn't find information about this event",
        type: 'text'
      });
      return;
    }

    await this.handleEventLocationRequest(userId, context, event);
  }

  private async handleLanguageSwitch(userId: string, context: ConversationContext, language: 'ar' | 'en') {
    context.userData.preferredLanguage = language;
    
    if (language === 'ar') {
      this.addBotMessage(userId, {
        text: "🌟 أهلاً وسهلاً! أنا مساعدك الذكي لمنصة الفعاليات الأولى في الأردن.\n\nيمكنني مساعدتك في:\n🎫 اكتشاف فعاليات رائعة\n🎟️ حجز التذاكر فوراً\n💬 الإجابة على استفساراتك\n🎯 الحصول على توصيات شخصية\n\nكيف يمكنني مساعدتك اليوم؟",
        type: 'text',
        quickReplies: [
          { text: "🔍 ابحث عن فعاليات", action: "discover_events" },
          { text: "🎵 حفلات موسيقية", action: "filter_music" },
          { text: "🏢 فعاليات تجارية", action: "filter_business" },
          { text: "🌐 English", action: "switch_english" },
          { text: "❓ مساعدة ودعم", action: "support" }
        ]
      });
    } else {
      this.addBotMessage(userId, {
        text: "👋 Welcome back! I'm your AI assistant for Jordan's #1 Event Platform.\n\nI can help you with:\n🎫 Discover amazing events\n🎟️ Book tickets instantly\n💬 Answer your questions\n🎯 Get personalized recommendations\n\nHow can I assist you today?",
        type: 'text',
        quickReplies: [
          { text: "🔍 Find Events", action: "discover_events" },
          { text: "🎵 Music Events", action: "filter_music" },
          { text: "🏢 Business Events", action: "filter_business" },
          { text: "🌐 العربية", action: "switch_arabic" },
          { text: "❓ Help & Support", action: "support" }
        ]
      });
    }
  }
}

export const whatsappChatService = new WhatsAppChatService();
export type { ChatMessage, ConversationContext, QuickReply }; 